<script setup lang="ts">
import { ElMessage, ElMessageBox } from "element-plus";
import { computed, ref } from "vue";
import {
  ApprovalRule,
  FormDefinition,
  FormRule,
  FormRuleType,
  RequiresReviewRule,
  RestrictedRule,
  SectionAssignables,
} from "~/api";
import { formatDate } from "~/utils/dateUtils";
import { showError } from "~/utils/handleAPIError";

const form = defineModel<FormDefinition>("form", { required: true });
const props = defineProps<{
  ruleType: FormRuleType;
  assignables: SectionAssignables;
  readOnly?: boolean;
}>();

type LabelValue = { label: string; value: string };
const whoType = ref<LabelValue>();
const numberRequiredInput = ref<number>(1);
const byDateInput = ref<Date>();

// Two objects in the array, one for users and one for roles
const optionGroups = computed(() => {
  return [
    {
      label: "Groups",
      items: props.assignables.groups?.map((group) => ({
        value: group.key,
        label: group.value,
        combo: { label: "Groups", value: group.key },
      })),
    },
    {
      label: "Roles",
      items: props.assignables.roles?.map((role) => ({
        value: role.key,
        label: role.value,
        combo: { label: "Roles", value: role.key },
      })),
    },
    {
      label: "Users",
      items: props.assignables.users?.map((user) => ({
        value: user.key,
        label: user.value,
        combo: { label: "Users", value: user.key },
      })),
    },
    {
      label: "Entities",
      items: [
        {
          value: "current-entity",
          label: "Current Entity",
          combo: { label: "Entities", value: "current-entity" },
        },
        // ...(props.assignables.entities?.map((entity) => ({
        //   value: entity.key,
        //   label: entity.value,
        //   combo: { label: "Entities", value: entity.key },
        // })) || []),
      ],
    },
  ];
});

const handleAddRule = () => {
  if (!form.value) {
    showError("Form not found");
    return;
  }

  if (!whoType.value?.value) {
    ElMessage.warning("Please select a user or role");
    return;
  }

  const assignedTo = whoType.value;

  if (assignedTo.value === "current-entity") {
    const currentEntityRuleExists = form.value.formRules.find(
      (rule) =>
        rule.ruleType === props.ruleType &&
        rule.appliedTo === "current-entity" &&
        rule.roleGroupOrIndividual === "Entities",
    );

    if (currentEntityRuleExists) {
      ElMessage.warning("Current Entity rule already exists");
      return;
    }
  } else {
    // check if the rule already exists
    const ruleExists = form.value.formRules.find(
      (rule) =>
        rule.ruleType === props.ruleType &&
        rule.appliedTo === assignedTo.value &&
        rule.roleGroupOrIndividual === assignedTo.label,
    );

    if (ruleExists) {
      ElMessage.warning("Rule already exists");
      return;
    }
  }

  const newRule: ApprovalRule | RequiresReviewRule | RestrictedRule = {
    ruleType: props.ruleType,
    appliedTo: assignedTo.value,
    roleGroupOrIndividual: assignedTo.label,
    formDefinitionGuid: form.value.guid,
    // @ts-expect-error: guid will be set by the server
    guid: undefined,
  };

  form.value.formRules.push(newRule);

  if (
    props.ruleType === FormRuleType.REQUIRES_APPROVAL ||
    props.ruleType === FormRuleType.REQUIRES_REVIEW
  ) {
    (newRule as ApprovalRule).numberRequired = numberRequiredInput.value;
    (newRule as ApprovalRule).byDate = byDateInput.value
      ?.toISOString()
      .split("T")[0];
  }
};

const roleSelected = computed<boolean>(() => {
  // if (!whoType.value?.label) return false;
  // if rule.appliedTo is from the roles in the options array, return true
  return whoType.value?.label === "Roles";
});

const nameTypeOf = (rule: FormRule) => {
  switch (rule.roleGroupOrIndividual) {
    case "Users":
      return {
        t: "User",
        n: props.assignables.users?.find((u) => u.key === rule.appliedTo)
          ?.value,
      };
    case "Roles":
      return {
        t: "Role",
        n: props.assignables.roles?.find((r) => r.key === rule.appliedTo)
          ?.value,
      };
    case "Groups":
      return {
        t: "Group",
        n: props.assignables.groups?.find((g) => g.key === rule.appliedTo)
          ?.value,
      };
    case "Entities":
      if (rule.appliedTo === "current-entity") {
        return {
          t: "Entity",
          n: "Current Entity",
        };
      }
      return {
        t: "Entity",
        n: props.assignables.entities?.find((e) => e.key === rule.appliedTo)
          ?.value,
      };
    default:
      return { t: "?", n: rule.appliedTo };
  }
};

const localRules = computed(() => {
  return form.value.formRules.filter(
    (rule) => rule.ruleType === props.ruleType,
  );
});

const handleDeleteRule = async (rule: FormRule) => {
  if (!form.value) {
    showError("Form not found");
    return;
  }
  ElMessageBox.confirm(
    "This will permanently delete this rule. Continue?",
    `Deleting Rule`,
    {
      confirmButtonText: "OK",
      cancelButtonText: "Cancel",
      closeOnPressEscape: false,
      closeOnClickModal: false,
      showClose: false,
      type: "warning",
    },
  ).then(async () => {
    const index = form.value.formRules.findIndex((r) => r === rule);
    if (index > -1) {
      form.value.formRules.splice(index, 1);
    }
  });
};
</script>

<template id="form-simple-text-option">
  <div class="add-rule">
    <el-select
      v-model="whoType"
      size="small"
      placeholder="Group/User/Role"
      value-key="value"
      :disabled="props.readOnly"
      @change="() => (numberRequiredInput = 1)"
    >
      <el-option-group
        v-for="optionGroup in optionGroups"
        :key="`${optionGroup.label}`"
        :label="optionGroup.label"
      >
        <el-option
          v-for="item in optionGroup.items"
          :key="`${optionGroup.label}-${item.value}`"
          :label="item.label ?? undefined"
          :value="item.combo"
        />
      </el-option-group>
    </el-select>

    <el-input-number
      v-if="roleSelected && ruleType !== FormRuleType.IS_RESTRICTED_TO"
      v-model="numberRequiredInput"
      controls-position="right"
      size="small"
      placeholder="Number required"
      :min="1"
    >
    </el-input-number>

    <el-date-picker
      v-if="ruleType !== FormRuleType.IS_RESTRICTED_TO"
      v-model="byDateInput"
      type="date"
      placeholder="When is this required by?"
      size="small"
    />

    <el-button
      size="small"
      :disabled="!(whoType && numberRequiredInput) || props.readOnly"
      @click="handleAddRule"
    >
      Add
    </el-button>
  </div>

  <div class="rule-list">
    <el-table :data="localRules" style="width: 100%" empty-text="Unrestricted">
      <el-table-column label="Only visible to" width="250">
        <template #default="scope">
          <span class="nameType">{{ nameTypeOf(scope.row).t }}</span>
          {{ nameTypeOf(scope.row).n }}
        </template>
      </el-table-column>
      <el-table-column
        v-if="ruleType !== FormRuleType.IS_RESTRICTED_TO"
        prop="numberRequired"
        label="How Many?"
        width="100"
      />
      <el-table-column
        v-if="ruleType !== FormRuleType.IS_RESTRICTED_TO"
        prop="byDate"
        label="By When?"
      >
        <template #default="scope">
          {{ formatDate(scope.row.byDate, 4) }}
        </template>
      </el-table-column>

      <el-table-column fixed="right" label="Action" min-width="50">
        <template #default="scope">
          <el-button
            link
            type="primary"
            size="small"
            :disabled="props.readOnly"
            @click="handleDeleteRule(scope.row)"
          >
            Remove
          </el-button>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<style lang="less">
.add-rule {
  display: flex;
  gap: 5px;
  margin-top: 10px;
  color: #757575;
  font-size: 0.9em;

  .el-select {
    width: 200px;
  }
}

.rule-list {
  margin-top: 10px;
  color: #757575;
  font-size: 0.9em;

  .nameType {
    margin-right: 5px;
    opacity: 0.7;
    font-size: 0.8em;
  }
}
</style>
